import { useMemo } from "react";

/**
 * Applies two-stage filtering to a data array:
 *   1. filterPredicate — structural filters
 *   2. getSearchableFields — global text-search over caller-defined fields
 *
 * @param {Object} options
 * @param {Array} options.data - Raw data array from the server
 * @param {Object} options.tableFilters - Active filter state from useTableBehavior
 * @param {Object} options.searchTextFilter - Text-search filter from useTableBehavior
 * @param {Function} [options.filterPredicate] - (item, tableFilters) => boolean.
 *                                               Omit when no structural filters are needed.
 *                                               It's necessary useCallback / module-level to
 *                                               avoid unnecessary recomputation.
 * @param {Function} options.getSearchableFields - (item) => string[].
 *                                                 Returns the field values to match the search
 *                                                 text against. It's necessary useCallback / module-level
 *                                                 to avoid unnecessary recomputation.
 *
 * @returns {{ filteredData: Array, fullyFilteredData: Array }}
 *   filteredData — after structural filters only (use as table `data` prop)
 *   fullyFilteredData — after structural + text filters (use for accurate exports)
 */
export const useFilteredTableData = ({
  data,
  tableFilters,
  searchTextFilter,
  filterPredicate,
  getSearchableFields,
}) => {
  const filteredData = useMemo(() => {
    if (!data) return [];

    if (!filterPredicate) return data;

    return data.filter((item) => filterPredicate(item, tableFilters));
  }, [data, tableFilters, filterPredicate]);

  const fullyFilteredData = useMemo(() => {
    const searchText = searchTextFilter?.global?.value?.trim().toLowerCase();

    if (!searchText) return filteredData;

    return filteredData.filter((item) =>
      getSearchableFields(item).some((field) =>
        field?.toLowerCase().includes(searchText),
      ),
    );
  }, [filteredData, searchTextFilter, getSearchableFields]);

  return { filteredData, fullyFilteredData };
};
